import { memo, useCallback } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SendIcon from "@mui/icons-material/Send";
import ListAltIcon from "@mui/icons-material/ListAlt";

import PageHero from "src-next/shared/ui/sections/PageHero";
import {
  QuestionnaireFormRenderer,
  type QuestionPreviewItem,
} from "src-next/shared/ui/questionnaire-form-renderer";
import { parseQuestionOptions } from "src-next/shared/utils/questionnaireOptions";

import type { UseQuestionnaireRespondResult } from "../hooks/useQuestionnaireRespond";

export type QuestionnaireRespondViewProps = UseQuestionnaireRespondResult & {
  variant?: "default" | "patientPortal";
  /** Ex.: voltar à home e limpar flag do fluxo por link público. */
  onLeavePlatform?: () => void;
  /**
   * Texto opcional (ex.: fluxo responsável respondendo pelo dependente).
   * Mostrado como alerta informativo acima do cartão do formulário.
   */
  respondentContextHint?: string;
};

const EMPTY_OPTIONS_MESSAGE = "Nenhuma opção definida para esta pergunta.";
const FORM_COLUMN_MAX_WIDTH = 860;
const centeredFormColumnSx = {
  width: "100%",
  maxWidth: FORM_COLUMN_MAX_WIDTH,
  mx: "auto",
} as const;

function isMultipleAnswersEnabled(question: QuestionPreviewItem): boolean {
  return (
    question.type === "multipleChoice" && question.allowMultipleAnswers === true
  );
}

const QuestionnaireRespondView = memo((props: QuestionnaireRespondViewProps) => {
  const {
    variant = "default",
    onLeavePlatform,
    respondentContextHint,
    questionnaireTitle,
    questionnaireDescription,
    previewItems,
    control,
    controller: Controller,
    isLoadingQuestionnaire,
    hasLoadError,
    isSubmittingResponse,
    hasSubmitError,
    hasQuestionnaire,
    handleSubmitResponse,
  } = props;

  const pageTitle =
    variant === "patientPortal"
      ? questionnaireTitle || "Questionário"
      : "Responder questionário";
  const pageSubtitle =
    variant === "patientPortal"
      ? "Preencha as respostas abaixo. Suas informações serão enviadas à equipe de saúde."
      : "Preencha as respostas e valide o payload de submissão no console.";

  const renderQuestionInput = useCallback(
    (question: QuestionPreviewItem, questionIndex: number) => {
      const fieldName = `answers.${questionIndex}.value` as const;
      const options = parseQuestionOptions(question.optionsCsv);

      return (
        <Controller
          key={question.id}
          control={control}
          name={fieldName}
          rules={{
            validate: (value) => {
              if (!question.isRequired) return true;
              if (isMultipleAnswersEnabled(question)) {
                const selectedValues = Array.isArray(value)
                  ? value
                      .map((selectedValue) => String(selectedValue).trim())
                      .filter((selectedValue) => selectedValue.length > 0)
                  : [];
                if (selectedValues.length > 0) return true;
                return "Resposta obrigatória.";
              }

              if (String(value ?? "").trim().length > 0) return true;
              return "Resposta obrigatória.";
            },
          }}
          render={({ field, fieldState }) => {
            if (question.type === "yesNo") {
              return (
                <Stack spacing={0.75}>
                  <RadioGroup row {...field}>
                    <FormControlLabel value="yes" control={<Radio />} label="Sim" />
                    <FormControlLabel value="no" control={<Radio />} label="Não" />
                  </RadioGroup>
                  {fieldState.error?.message && (
                    <Typography variant="caption" color="error.main">
                      {fieldState.error.message}
                    </Typography>
                  )}
                </Stack>
              );
            }

            if (question.type === "multipleChoice") {
              const shouldAllowMultipleAnswers = isMultipleAnswersEnabled(question);
              const selectedValues = Array.isArray(field.value)
                ? field.value
                    .map((selectedValue) => String(selectedValue).trim())
                    .filter((selectedValue) => selectedValue.length > 0)
                : [];
              const selectedSingleValue = Array.isArray(field.value)
                ? ""
                : String(field.value ?? "");

              return (
                <Stack spacing={0.75}>
                  {options.length > 0 ? (
                    shouldAllowMultipleAnswers ? (
                      <Stack spacing={0.25}>
                        {options.map((option) => {
                          const isChecked = selectedValues.includes(option);

                          return (
                            <FormControlLabel
                              key={option}
                              control={
                                <Checkbox
                                  checked={isChecked}
                                  name={field.name}
                                  inputRef={option === options[0] ? field.ref : undefined}
                                  onBlur={field.onBlur}
                                  onChange={(event) => {
                                    const shouldCheck = event.target.checked;
                                    const nextValues = shouldCheck
                                      ? [...selectedValues, option]
                                      : selectedValues.filter(
                                          (value) => value !== option,
                                        );
                                    field.onChange(nextValues);
                                  }}
                                />
                              }
                              label={option}
                            />
                          );
                        })}
                      </Stack>
                    ) : (
                      <RadioGroup
                        name={field.name}
                        onBlur={field.onBlur}
                        value={selectedSingleValue}
                        onChange={(_event, nextValue) => field.onChange(nextValue)}
                      >
                        {options.map((option, optionIndex) => (
                          <FormControlLabel
                            key={option}
                            value={option}
                            control={
                              <Radio
                                inputRef={
                                  optionIndex === 0 ? field.ref : undefined
                                }
                              />
                            }
                            label={option}
                          />
                        ))}
                      </RadioGroup>
                    )
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {EMPTY_OPTIONS_MESSAGE}
                    </Typography>
                  )}
                  {fieldState.error?.message && (
                    <Typography variant="caption" color="error.main">
                      {fieldState.error.message}
                    </Typography>
                  )}
                </Stack>
              );
            }

            return (
              <TextField
                {...field}
                label="Resposta"
                placeholder="Escreva aqui..."
                fullWidth
                error={Boolean(fieldState.error?.message)}
                helperText={fieldState.error?.message}
                multiline={question.type === "longText"}
                minRows={question.type === "longText" ? 5 : 1}
              />
            );
          }}
        />
      );
    },
    [Controller, control],
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: "calc(100vh - 96px)" }}>
      <Stack spacing={3} sx={centeredFormColumnSx}>
        {variant === "patientPortal" && onLeavePlatform ? (
          <Stack direction="row" justifyContent="flex-start">
            <Typography
              component="button"
              type="button"
              variant="body2"
              onClick={onLeavePlatform}
              sx={{
                cursor: "pointer",
                border: "none",
                background: "none",
                p: 0,
                color: "primary.main",
                textDecoration: "underline",
                textAlign: "left",
              }}
            >
              Voltar à plataforma
            </Typography>
          </Stack>
        ) : null}

        <PageHero
          title={pageTitle}
          subtitle={pageSubtitle}
          icon={<ListAltIcon sx={{ fontSize: 40 }} />}
        />

        {respondentContextHint ? (
          <Alert severity="info" sx={{ width: "100%" }}>
            {respondentContextHint}
          </Alert>
        ) : null}

        <Box sx={centeredFormColumnSx}>
          <Card
            variant="outlined"
            sx={{
              width: "100%",
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              {isLoadingQuestionnaire ? (
                <Typography variant="body2" color="text.secondary">
                  Carregando questionário...
                </Typography>
              ) : hasLoadError ? (
                <Alert severity="error">
                  Não foi possível carregar o questionário. Tente novamente.
                </Alert>
              ) : !hasQuestionnaire ? (
                <Alert severity="warning">
                  Questionário não encontrado para o identificador recebido.
                </Alert>
              ) : (
                <Stack spacing={2.5}>
                  <QuestionnaireFormRenderer
                    questions={previewItems}
                    title={questionnaireTitle}
                    description={questionnaireDescription}
                    renderQuestionInput={renderQuestionInput}
                    maxWidth={760}
                    questionCardProps={{ sx: { borderLeftColor: "primary.main" } }}
                  />

                  {hasSubmitError && (
                    <Alert severity="error">
                      Não foi possível enviar as respostas. Revise os dados e tente
                      novamente.
                    </Alert>
                  )}

                  <Stack direction="row" justifyContent="flex-end">
                    <LoadingButton
                      variant="contained"
                      onClick={handleSubmitResponse}
                      loading={isSubmittingResponse}
                      loadingPosition="start"
                      startIcon={<SendIcon />}
                    >
                      Enviar respostas
                    </LoadingButton>
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Box>
      </Stack>
    </Box>
  );
});

QuestionnaireRespondView.displayName = "QuestionnaireRespondView";

export default QuestionnaireRespondView;
